import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, useApi } from '../api';
import {
  Button,
  Card,
  ErrorBanner,
  Loading,
  StatTile,
  StatusBadge,
  TablaResponsiva,
} from '../components/ui';
import { useAviso } from '../avisos';
import { useT } from '../i18n';

/**
 * Comparativa de plataformas entre todas las influencers.
 *
 * ── Qué responde que las fichas no responden ────────────────────────────────────
 * La pestaña Plataformas de cada influencer dice **cómo le va a ésa**. La pregunta que no
 * tenía dónde responderse es **a cuál le va mejor**, y para eso hay que verlas juntas: con
 * seis pestañas abiertas nadie compara nada.
 *
 * ── Por qué se distingue lo que dice Meta de lo que sabemos nosotros ────────────
 * Seguidores y alcance salen de Instagram y envejecen; lo publicado y lo pendiente son
 * nuestros y se saben sin preguntar. Mezclarlos en una sola tabla sin marcar la diferencia
 * haría creer que las columnas valen lo mismo — y una fila sin conexión tendría huecos donde
 * en realidad hay dato.
 */

type AccountMetrics = {
  username: string | null;
  accountType: string | null;
  followers: number | null;
  follows: number | null;
  mediaCount: number | null;
  reach: number | null;
};

type PlatformRow = {
  creatorId: string;
  publicName: string;
  status: string;
  niche: string[];
  instagram: { status: string; username: string | null; profileUrl: string | null } | null;
  facebook: { status: string; username: string | null; profileUrl: string | null } | null;
  metrics: AccountMetrics | null;
  metricsError: string | null;
  facebookMetrics: AccountMetrics | null;
  facebookMetricsError: string | null;
  publishedCount: number;
  pendingCount: number;
  facebookPublishedCount: number;
  facebookPendingCount: number;
  contentCount: number;
  /**
   * Si ya existía o la generamos nosotros.
   *
   * Comparten tabla porque comparar exige las mismas columnas, pero «0 publicaciones» significa
   * lo opuesto en cada una: en una propia es trabajo que falta, en una externa es que sus
   * publicaciones no pasaron por acá. Sin la distinción, la columna miente sobre la mitad de
   * las filas.
   */
  external: boolean;
};

type OverviewData = { rows: PlatformRow[]; connected: number; withoutMetrics: number };

/** Las columnas por las que tiene sentido ordenar, con de dónde sale cada número. */
const SORTS = [
  { key: 'followers', label: 'Seguidores', origin: 'instagram' },
  { key: 'ratio', label: 'Ratio', origin: 'instagram' },
  { key: 'reach', label: 'Alcance', origin: 'instagram' },
  { key: 'publishedCount', label: 'Publicadas', origin: 'local' },
  { key: 'pendingCount', label: 'Pendientes', origin: 'local' },
] as const;

/**
 * Seguidores por cada cuenta seguida.
 *
 * Es la medida de autoridad que se lee de un vistazo: una cuenta con 8.000 seguidores que
 * sigue a 7.000 creció devolviendo seguimientos, y una que sigue a 200 creció porque la
 * buscaron. El número de seguidores solo no distingue esas dos cosas.
 *
 * `null` cuando no se puede calcular: sin métricas, o siguiendo a nadie —dividir por cero
 * daría infinito y una cuenta nueva aparecería primera en el ranking.
 */
function ratioOf(row: PlatformRow, vista: Vista): number | null {
  const metrics = comoVista(row, vista).metrics;
  const seguidores = metrics?.followers ?? null;
  const seguidos = metrics?.follows ?? null;
  if (seguidores === null || seguidos === null || seguidos === 0) return null;
  return seguidores / seguidos;
}

type SortKey = (typeof SORTS)[number]['key'];

/** Qué pestaña se está mirando. El resumen suma las dos plataformas; las otras muestran la suya. */
type Vista = 'resumen' | 'instagram' | 'facebook';

function valueOf(row: PlatformRow, key: SortKey, vista: Vista = 'instagram'): number {
  const v = comoVista(row, vista);
  if (key === 'followers') return v.metrics?.followers ?? -1;
  if (key === 'ratio') return ratioOf(row, vista) ?? -1;
  if (key === 'reach') return v.metrics?.reach ?? -1;
  if (key === 'publishedCount') return v.publicadas;
  return v.pendientes;
}

/**
 * La fila vista desde una plataforma.
 *
 * La tabla dibuja las mismas columnas para las dos, así que en vez de duplicarla se le da la
 * fila ya traducida. Sin esto, cada celda tendría que preguntar en qué pestaña está — y la
 * próxima plataforma obligaría a tocar todas.
 */
function comoVista(
  row: PlatformRow,
  vista: Vista,
): {
  cuenta: { status: string; username: string | null; profileUrl: string | null } | null;
  metrics: AccountMetrics | null;
  error: string | null;
  publicadas: number;
  pendientes: number;
} {
  if (vista === 'facebook') {
    return {
      cuenta: row.facebook,
      metrics: row.facebookMetrics,
      error: row.facebookMetricsError,
      publicadas: row.facebookPublishedCount,
      pendientes: row.facebookPendingCount,
    };
  }

  if (vista === 'instagram') {
    return {
      cuenta: row.instagram,
      metrics: row.metrics,
      error: row.metricsError,
      publicadas: row.publishedCount,
      pendientes: row.pendingCount,
    };
  }

  /*
    En el resumen, cada fila suma las dos plataformas de ESA influencer.

    Antes mostraba sólo Instagram: los totales de arriba sumaban y la tabla de abajo no, así que
    la suma de la columna no daba el total de la cabecera. Una tabla que se contradice con su
    propio encabezado es peor que una incompleta.

    `null` no es cero, igual que en Estadísticas: si ninguna de las dos informa un campo, el
    total queda en nulo y la celda muestra un guion. Sumarlo como cero convertiría «no se sabe»
    en «ninguno», y de ahí salen ratios inventados.
  */
  const sumar = (clave: keyof AccountMetrics): number | null => {
    const valores = [row.metrics?.[clave], row.facebookMetrics?.[clave]].filter(
      (valor): valor is number => typeof valor === 'number',
    );
    return valores.length === 0 ? null : valores.reduce((total, valor) => total + valor, 0);
  };

  return {
    // La cuenta que se muestra es la de Instagram: es la que tiene usuario y enlace público.
    cuenta: row.instagram ?? row.facebook,
    metrics:
      row.metrics || row.facebookMetrics
        ? {
            username: row.metrics?.username ?? row.facebookMetrics?.username ?? null,
            accountType: null,
            followers: sumar('followers'),
            follows: sumar('follows'),
            mediaCount: sumar('mediaCount'),
            reach: sumar('reach'),
          }
        : null,
    error: row.metricsError ?? row.facebookMetricsError,
    publicadas: row.publishedCount + row.facebookPublishedCount,
    pendientes: row.pendingCount + row.facebookPendingCount,
  };
}

function formatNumber(value: number | null): string {
  return value === null ? '—' : value.toLocaleString('es-AR');
}

export function Platforms() {
  const { t } = useT();
  // Sin refresco automático: cada carga son tantas llamadas a Meta como influencers
  // conectadas, y consumen el límite de tasa de la app. Se pide cuando alguien mira.
  const state = useApi<OverviewData>('/api/platforms/overview');
  const [sort, setSort] = useState<SortKey>('followers');
  /**
   * Qué se está mirando.
   *
   * El resumen responde «cómo vamos» sumando las dos; las pestañas responden «cómo va cada una».
   * Son dos preguntas distintas y mezclarlas en una tabla obliga a duplicar cada columna.
   */
  const [vista, setVista] = useState<Vista>('resumen');

  if (state.loading && !state.data) return <Loading label="Consultando a Instagram…" />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!state.data) return null;

  const data = state.data;
  const rows = [...data.rows].sort((a, b) => valueOf(b, sort, vista) - valueOf(a, sort, vista));

  /*
    Los totales, por plataforma y combinados.

    Los seguidores se pueden sumar entre Instagram y una Página: es el mismo concepto y la suma
    significa algo. Alcance y cuentas seguidas no —una Página no sigue a nadie y su alcance se
    mide con otras métricas— así que el resumen no los muestra en vez de inventar un total.
  */
  const suma = (f: (row: PlatformRow) => number): number => rows.reduce((n, r) => n + f(r), 0);

  const igSeguidores = suma((r) => r.metrics?.followers ?? 0);
  const fbSeguidores = suma((r) => r.facebookMetrics?.followers ?? 0);

  /*
    Los demás campos que se pueden sumar.

    `null` no es cero: la suma de una columna donde nadie informó nada tiene que quedar en
    guion, no en cero. Por eso se cuenta aparte cuántas cuentas informaron el campo — si no lo
    informó ninguna, no hay total que mostrar.
  */
  const sumaOpcional = (f: (row: PlatformRow) => number | null | undefined): number | null => {
    const valores = rows.map(f).filter((valor): valor is number => typeof valor === 'number');
    return valores.length === 0 ? null : valores.reduce((total, valor) => total + valor, 0);
  };

  const igAlcance = sumaOpcional((r) => r.metrics?.reach);
  const fbAlcance = sumaOpcional((r) => r.facebookMetrics?.reach);
  const igPiezas = sumaOpcional((r) => r.metrics?.mediaCount);
  const fbPiezas = sumaOpcional((r) => r.facebookMetrics?.mediaCount);

  /** El total de dos sumas opcionales, en guion si ninguna existe. */
  const total = (a: number | null, b: number | null): string =>
    a === null && b === null ? '—' : formatNumber((a ?? 0) + (b ?? 0));

  const detalle = (a: number | null, b: number | null): string =>
    `Instagram ${a === null ? '—' : formatNumber(a)} · Facebook ${b === null ? '—' : formatNumber(b)}`;
  const igPublicadas = suma((r) => r.publishedCount);
  const fbPublicadas = suma((r) => r.facebookPublishedCount);
  const igPendientes = suma((r) => r.pendingCount);
  const fbPendientes = suma((r) => r.facebookPendingCount);

  const esFacebook = vista === 'facebook';
  const totalFollowers = esFacebook ? fbSeguidores : igSeguidores;
  const totalPublished = esFacebook ? fbPublicadas : igPublicadas;
  const totalPending = esFacebook ? fbPendientes : igPendientes;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{t('plat.titulo')}</h1>
          <p className="mt-1 text-sm text-[var(--color-ink-muted)]">{t('plat.bajada')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MedirAhora onDone={state.reload} />
          <AgregarExterna onDone={state.reload} />
        </div>
      </div>

      <div className="flex gap-2">
        {(
          [
            ['resumen', t('plat.resumen')],
            ['instagram', 'Instagram'],
            ['facebook', 'Facebook'],
          ] as const
        ).map(([clave, nombre]) => (
          <button
            key={clave}
            type="button"
            onClick={() => setVista(clave)}
            className={`rounded-xl border px-3 py-1.5 text-sm ${
              vista === clave
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-[var(--color-border-subtle)] text-[var(--color-ink-muted)]'
            }`}
          >
            {nombre}
          </button>
        ))}
      </div>

      {/*
        El resumen suma todo lo sumable, y cada total lleva su desglose.

        Los que no se suman no están por omisión sino por criterio: «seguidos» no, porque una
        Página no sigue a nadie y el total sería el de Instagram disfrazado de total; el ratio
        tampoco, por lo mismo. Donde Facebook no informa un campo, el desglose muestra «—» y el
        total suma lo que hay: se ve de dónde sale el número en vez de tener que deducirlo.
      */}
      {vista === 'resumen' && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
          <StatTile
            label="Seguidores"
            value={formatNumber(igSeguidores + fbSeguidores)}
            hint={`Instagram ${formatNumber(igSeguidores)} · Facebook ${formatNumber(fbSeguidores)}`}
          />
          <StatTile
            label={t('plat.alcance')}
            value={total(igAlcance, fbAlcance)}
            hint={detalle(igAlcance, fbAlcance)}
          />
          <StatTile
            label={t('plat.piezas')}
            value={total(igPiezas, fbPiezas)}
            hint={detalle(igPiezas, fbPiezas)}
          />
          <StatTile
            label={t('plat.publicadas')}
            value={igPublicadas + fbPublicadas}
            hint={`Instagram ${igPublicadas} · Facebook ${fbPublicadas}`}
          />
          <StatTile
            label={t('plat.pendientes')}
            value={igPendientes + fbPendientes}
            tone={igPendientes + fbPendientes > 0 ? 'warn' : 'neutral'}
            hint={`Instagram ${igPendientes} · Facebook ${fbPendientes}`}
          />
        </div>
      )}

      {/* Dos columnas en el teléfono: tres cifras cortas no justifican tres pantallazos. */}
      {vista !== 'resumen' && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
          <StatTile
            label={t('plat.seguidores')}
            value={formatNumber(totalFollowers)}
            hint={t(data.connected === 1 ? 'plat.sumaDeCuentaUna' : 'plat.sumaDeCuentas', {
              n: data.connected,
            })}
          />
          <StatTile label={t('plat.publicadas')} value={totalPublished} />
          <StatTile
            label={t('plat.pendientes')}
            value={totalPending}
            tone={totalPending > 0 ? 'warn' : 'neutral'}
          />
        </div>
      )}

      {/*
        Sobre cuántas se está comparando, cuando no son todas.

        Ordenar por seguidores con tres cuentas sin responder es una conclusión sobre parte de
        los datos. Decirlo cuesta una línea; no decirlo convierte la tabla en un ranking que
        parece completo.
      */}
      {data.withoutMetrics > 0 && (
        <div className="rounded-2xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 p-4 text-sm">
          {t('plat.sinMetricas', { cuantas: data.withoutMetrics, total: data.rows.length })}
        </div>
      )}

      <Card title={t('plat.comparativa')}>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs text-[var(--color-ink-muted)]">{t('plat.ordenarPor')}</span>
          {SORTS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setSort(option.key)}
              className={
                'rounded-lg px-3 py-1.5 text-xs font-medium transition ' +
                (sort === option.key
                  ? 'bg-[var(--color-accent)] text-white'
                  : 'border border-[var(--color-border-subtle)] hover:border-[var(--color-border-strong)]')
              }
            >
              {option.label}
            </button>
          ))}
        </div>

        {/*
          Nueve columnas no caben en un teléfono, y deslizarlas no lo arregla: el encabezado se
          va de la pantalla antes que el dato, y quedan nueve números sin nombre. Debajo de `md`
          esto se dibuja como una tarjeta por fila, con cada cifra rotulada. Ver `TablaResponsiva`.
        */}
        <TablaResponsiva
          filas={rows}
          claveDe={(row) => row.creatorId}
          columnas={[
            {
              clave: 'influencer',
              encabezado: 'Influencer',
              titulo: true,
              celda: (row) => (
                <>
                  {row.external ? (
                    // Su ficha redirige acá: enlazarla sería un viaje de ida y vuelta.
                    <span className="font-medium">{row.publicName}</span>
                  ) : (
                    <>
                      {/*
                      El nombre lleva a las estadísticas y no a la ficha: esta pantalla es la de
                      rendimiento. La ficha sigue a un clic, en el enlace de al lado.
                    */}
                      <Link
                        to={`/influencer/${row.creatorId}/estadisticas`}
                        className="font-medium hover:text-[var(--color-accent)]"
                      >
                        {row.publicName}
                      </Link>
                      <Link
                        to={`/influencer/${row.creatorId}`}
                        className="ml-2 text-xs text-[var(--color-ink-faint)] hover:text-[var(--color-accent)]"
                      >
                        ficha
                      </Link>
                    </>
                  )}
                  {row.external && (
                    <>
                      <span className="ml-2 rounded-full bg-[var(--color-surface-sunken)] px-2 py-0.5 text-[0.65rem] font-medium text-[var(--color-ink-muted)]">
                        externa
                      </span>
                      {/*
                      Quitarla, desde acá. Es la única pantalla donde existe: sin este botón,
                      sacar una agregada por error exigiría tocar la base.
                    */}
                      <button
                        type="button"
                        onClick={() => {
                          if (!window.confirm(t('plat.confirmarQuitar', { quien: row.publicName })))
                            return;
                          void api.del(`/api/creators/${row.creatorId}`).then(state.reload);
                        }}
                        className="ml-2 text-[0.65rem] text-[var(--color-ink-muted)] underline underline-offset-2 hover:text-[var(--color-danger)]"
                      >
                        {t('plat.quitar')}
                      </button>
                    </>
                  )}
                  <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">
                    {row.external
                      ? t('plat.externa')
                      : row.niche.slice(0, 2).join(' · ')}
                  </p>
                </>
              ),
            },
            {
              clave: 'cuenta',
              encabezado: t('plat.cuenta'),
              celda: (row) => {
                const v = comoVista(row, vista);
                return (
                  <>
                    {v.cuenta?.profileUrl ? (
                      <a
                        href={v.cuenta.profileUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-xs hover:text-[var(--color-accent)]"
                      >
                        {vista === 'facebook'
                          ? v.cuenta.username || t('plat.laPagina')
                          : `@${v.cuenta.username}`}{' '}
                        ↗
                      </a>
                    ) : (
                      <StatusBadge status={v.cuenta?.status ?? 'NOT_CREATED'} />
                    )}
                    {/*
                  El motivo, no sólo el hueco. «—» en seguidores no distingue una cuenta sin
                  conectar de un token vencido, y son dos acciones distintas.
                */}
                    {v.error && (
                      <p className="mt-1 max-w-[16rem] text-xs text-[var(--color-warn)]">
                        {v.error}
                      </p>
                    )}
                    {/*
                  Conectar desde acá, que es donde se ve que falta. Es un enlace y no un botón
                  porque el OAuth es una navegación: sale del sitio y vuelve por el callback.
                */}
                    {v.cuenta && v.cuenta.status !== 'CONNECTED' && (
                      <a
                        href={`/api/integrations/${vista === 'facebook' ? 'facebook' : 'instagram'}/connect?creatorId=${row.creatorId}`}
                        className="mt-1 inline-block rounded-lg border border-[var(--color-accent)] px-2 py-1 text-xs font-medium text-[var(--color-accent)] transition hover:opacity-80"
                      >
                        {t('plat.conectarOAuth')}
                      </a>
                    )}
                  </>
                );
              },
            },
            {
              clave: 'seguidores',
              encabezado: t('plat.seguidores'),
              numerica: true,
              celda: (row) => formatNumber(comoVista(row, vista).metrics?.followers ?? null),
            },
            {
              clave: 'seguidos',
              encabezado: t('plat.seguidos'),
              numerica: true,
              celda: (row) => (
                <span className="text-[var(--color-ink-muted)]">
                  {formatNumber(comoVista(row, vista).metrics?.follows ?? null)}
                </span>
              ),
            },
            {
              clave: 'ratio',
              // Por encima de 1 la cuenta tiene más seguidores que seguidos, que es la señal
              // que se busca. Marcarlo evita comparar decimales a ojo.
              encabezado: <span title={t('plat.ratioTitulo')}>{t('plat.ratio')}</span>,
              numerica: true,
              celda: (row) => {
                /*
                  Leía `row.metrics` sin mirar la pestaña, así que en Facebook mostraba el ratio
                  de Instagram: una columna con datos de otra plataforma, sin nada que lo
                  delatara. Es peor que un hueco — un hueco se nota.
                */
                const ratio = ratioOf(row, vista);
                if (ratio === null) return '—';
                return (
                  <span className={ratio >= 1 ? 'font-medium text-[var(--color-ok)]' : ''}>
                    {ratio >= 10 ? ratio.toFixed(0) : ratio.toFixed(2)}×
                  </span>
                );
              },
            },
            {
              clave: 'alcance',
              encabezado: t('plat.alcance'),
              numerica: true,
              celda: (row) => formatNumber(comoVista(row, vista).metrics?.reach ?? null),
            },
            {
              clave: 'enInstagram',
              // Estaba fijo en «En Instagram» y las otras pestañas lo mostraban igual. En el
              // resumen la celda es la suma de las dos, así que nombrar una sería mentir.
              encabezado:
                vista === 'facebook'
                  ? t('plat.enFacebook')
                  : vista === 'instagram'
                    ? t('plat.enInstagram')
                    : t('plat.enSusCuentas'),
              numerica: true,
              celda: (row) => (
                <span className="text-[var(--color-ink-muted)]">
                  {formatNumber(comoVista(row, vista).metrics?.mediaCount ?? null)}
                </span>
              ),
            },
            {
              clave: 'publicadas',
              encabezado: 'Publicadas',
              numerica: true,
              celda: (row) => comoVista(row, vista).publicadas,
            },
            {
              clave: 'pendientes',
              encabezado: 'Pendientes',
              numerica: true,
              celda: (row) =>
                comoVista(row, vista).pendientes > 0 ? (
                  <span className="font-medium text-[var(--color-warn)]">
                    {comoVista(row, vista).pendientes}
                  </span>
                ) : (
                  comoVista(row, vista).pendientes
                ),
            },
          ]}
        />

        <p className="mt-4 text-xs text-[var(--color-ink-muted)]">
          <strong>{t('plat.ratio')}</strong>
          {t('plat.notaRatio')}
          <br />
          <strong>{t('plat.seguidores')}</strong>, <strong>{t('plat.seguidos')}</strong>,{' '}
          <strong>{t('plat.alcance')}</strong>
          {t('plat.notaFuentes')}
        </p>
      </Card>
    </div>
  );
}

/**
 * Da de alta una cuenta que ya existe, para medirla junto a las propias.
 *
 * ── Qué se puede y qué no ─────────────────────────────────────────────────────
 * Las mismas métricas que las propias, con la misma condición: que la cuenta se conecte por
 * OAuth. Instagram sólo entrega seguidores, alcance e impresiones de cuentas cuyo dueño autoriza
 * el acceso — no hay forma de leer las de una cuenta ajena, ni acá ni en ninguna herramienta.
 *
 * Así que esto sirve para las cuentas que administrás y que no salieron de este sistema. Para
 * las de otra persona está la pestaña Referencias, que guarda el enlace y nada más, y el
 * formulario lo dice antes de que alguien lo intente y crea que falló.
 */
function AgregarExterna({ onDone }: { onDone: () => void }) {
  const { t } = useT();
  const [abierto, setAbierto] = useState(false);
  const [nombre, setNombre] = useState('');
  const [usuario, setUsuario] = useState('');
  const [guardando, setGuardando] = useState(false);

  if (!abierto) {
    return (
      <Button variant="secondary" onClick={() => setAbierto(true)}>
        {t('plat.agregarExterna')}
      </Button>
    );
  }

  return (
    <div className="w-full max-w-md rounded-2xl border border-[var(--color-border-subtle)] p-4">
      <p className="text-sm font-medium">{t('plat.agregarExterna')}</p>
      <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
        {t('plat.externaQueAntes')}
        <strong>{t('plat.externaSoloFila')}</strong>
        {t('plat.externaQueDespues')}
      </p>

      <div className="mt-3 space-y-2">
        <input
          value={nombre}
          onChange={(event) => setNombre(event.target.value)}
          placeholder={t('plat.nombrePublico')}
          className="w-full rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm"
        />
        <input
          value={usuario}
          onChange={(event) => setUsuario(event.target.value)}
          placeholder={t('plat.usuarioIg')}
          className="w-full rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm"
        />
      </div>

      <div className="mt-3 flex gap-2">
        <Button
          disabled={guardando || nombre.trim().length < 2}
          onClick={() => {
            setGuardando(true);
            void api
              .post('/api/creators/external', {
                publicName: nombre.trim(),
                ...(usuario.trim() ? { username: usuario.trim() } : {}),
              })
              .then(() => {
                setAbierto(false);
                setNombre('');
                setUsuario('');
                onDone();
              })
              .finally(() => setGuardando(false));
          }}
        >
          {t(guardando ? 'plat.agregando' : 'plat.agregar')}
        </Button>
        <Button variant="secondary" onClick={() => setAbierto(false)}>
          {t('comun.cancelar')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Medir los seguidores ahora, sin esperar al cron diario.
 *
 * ── Por qué hacía falta un botón ──────────────────────────────────────────────
 * La ruta que captura métricas existía desde siempre y NADA en el panel la llamaba: la única
 * forma de que un número apareciera era esperar al cron de las 00:05 UTC. Una influencer creada
 * después de esa hora se veía sin seguidores durante casi un día, y no había manera de
 * distinguir «todavía no se midió» de «algo está roto» — ni de hacer nada al respecto.
 *
 * Pasó de verdad, y con un agravante: el cron diario llevaba semanas cayéndose antes de
 * ejecutarse, así que la espera no era de un día sino indefinida.
 *
 * ── Qué mide ──────────────────────────────────────────────────────────────────
 * Instagram, que es lo que la ruta captura. Facebook lo toma sólo el cron diario; esa asimetría
 * ya existía y no se toca desde acá — arreglarla es cambiar la ruta, no agregar un botón.
 */
function MedirAhora({ onDone }: { onDone: () => void }) {
  const { t } = useT();
  const aviso = useAviso();
  const [midiendo, setMidiendo] = useState(false);

  return (
    <Button
      variant="secondary"
      disabled={midiendo}
      onClick={() => {
        setMidiendo(true);
        void api
          .post<{ resultados: { ok: boolean }[] }>('/api/metricas/capturar')
          .then((resultado) => {
            const ok = resultado.resultados.filter((r) => r.ok).length;
            aviso.ok(t('plat.medidas', { n: ok }));
            onDone();
          })
          .catch(() => aviso.error(t('plat.noSePudoMedir')))
          .finally(() => setMidiendo(false));
      }}
    >
      {t(midiendo ? 'plat.midiendo' : 'plat.medirAhora')}
    </Button>
  );
}
